import {
  AreaSeries,
  ColorType,
  createChart,
  CrosshairMode,
  LineSeries,
  LineType,
} from 'lightweight-charts'
import { useEffect, useRef } from 'react'
import styled, { useTheme } from 'styled-components'

import { Trend } from '@root/entities/trend'

import { createChartData } from './summary-chart-util'

import type { Summary } from '@root/entities/summary/model'
import type { ClassNameProps } from '@root/shared/react'
import type { IChartApi, ISeriesApi } from 'lightweight-charts'

type SummaryChartProps = ClassNameProps & {
  changePercentage: number
  data: Array<Summary.DataEntry>
}

export const SummaryChart: React.FC<SummaryChartProps> = ({
  className,
  changePercentage,
  data,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartApiRef = useRef<IChartApi | undefined>(undefined)
  const seriesRef = useRef<ISeriesApi<'Area'> | undefined>(undefined)
  const placeholderSeriesRef = useRef<ISeriesApi<'Line'> | undefined>(undefined)
  const theme = useTheme()
  const bgColor =
    theme.appearance === 'dark' ? 'transparent' : 'rgba(255, 255, 255, 0)'

  const trend = Trend.of(changePercentage)

  useEffect(() => {
    const element = containerRef.current
    if (!element) {
      return
    }

    const chart = createChart(element, {
      grid: {
        horzLines: {
          visible: false,
        },
        vertLines: {
          visible: false,
        },
      },
      autoSize: true,
      layout: {
        background: {
          type: ColorType.Solid,
          color: 'transparent',
        },
        attributionLogo: false,
      },
      leftPriceScale: { visible: false },
      rightPriceScale: { visible: false },
      timeScale: { visible: false },
      crosshair: {
        mode: CrosshairMode.Hidden,
      },
      handleScroll: false,
      handleScale: false,
    })
    chartApiRef.current = chart

    const series = chart.addSeries(AreaSeries, {
      baseLineVisible: false,
      priceLineVisible: false,
      lineType: LineType.Simple,
      lineWidth: 2,
    })

    seriesRef.current = series

    const placeholderSeries = chart.addSeries(LineSeries, {
      visible: false,
    })

    placeholderSeriesRef.current = placeholderSeries

    chart.timeScale().fitContent()
    return () => {
      chart.remove()
      chartApiRef.current = undefined
      seriesRef.current = undefined
      placeholderSeriesRef.current = undefined
    }
  }, [])

  useEffect(() => {
    const series = seriesRef.current
    if (!series) {
      return
    }

    const color =
      trend === Trend.Down ? theme.palette.down[9] : theme.palette.up[9]

    series.applyOptions({
      lineColor: color,
      topColor: color,
      bottomColor: bgColor,
    })
  }, [theme, trend, bgColor])

  useEffect(() => {
    const series = seriesRef.current
    const chart = chartApiRef.current
    const placeholderSeries = placeholderSeriesRef.current
    if (!series || !chart || !placeholderSeries) {
      return
    }

    const { chartData, placeholderData } = createChartData(data)

    placeholderSeries.setData(placeholderData)
    series.setData(chartData)
    chart.timeScale().fitContent()
  }, [data])

  return <Root className={className} ref={containerRef} />
}

const Root = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 0;
`
